import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Loader2, Plus, Trash2 } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { mediaUrl } from "../../lib/content";
import MediaUpload from "./MediaUpload";

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/50 transition-colors";

const emptyDraft = { title: "", desc: "", intro: "", image: null };

export const ServicesTab = () => {
  const [services, setServices] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyDraft);

  const load = async () => {
    const { data } = await adminApi.get("/admin/services");
    setServices(data);
    const d = {};
    data.forEach((s) => {
      d[s.slug] = { title: s.title, desc: s.desc || "", intro: s.intro || "", image: s.image };
    });
    setDrafts(d);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async (slug) => {
    setSaving(slug);
    try {
      await adminApi.put(`/admin/services/${slug}`, drafts[slug]);
      toast.success("Service saved — live on the site.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const add = async () => {
    if (!newDraft.title.trim()) {
      toast.error("Please add a service title.");
      return;
    }
    setSaving("new");
    try {
      await adminApi.post("/admin/services", newDraft);
      toast.success("Service added — it now appears on the website.");
      setAdding(false);
      setNewDraft(emptyDraft);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}" from the website?`)) return;
    try {
      await adminApi.delete(`/admin/services/${s.slug}`);
      toast.success("Service deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const move = async (index, dir) => {
    const next = [...services];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setServices(next);
    try {
      await adminApi.post("/admin/services/reorder", { slugs: next.map((s) => s.slug) });
      toast.success("Order saved.");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const set = (slug, k) => (e) =>
    setDrafts((d) => ({ ...d, [slug]: { ...d[slug], [k]: e.target ? e.target.value : e } }));

  if (!services) {
    return <div className="py-16 text-center text-white/40 text-sm">Loading services…</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-white/40">{services.length} service{services.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => setAdding((a) => !a)}
          data-testid="add-service-button"
          className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors"
        >
          <Plus size={14} /> Add Service
        </button>
      </div>

      {adding && (
        <div data-testid="service-add-form" className="mb-6 border border-[#c8b99d]/30 bg-[#0d0d10] p-5 md:p-6">
          <h3 className="font-heading text-lg font-light text-white mb-5">Add Service</h3>
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
            <MediaUpload
              label="Card image"
              kind="image"
              value={newDraft.image}
              onChange={(v) => setNewDraft((d) => ({ ...d, image: v }))}
              testId="service-new-image"
            />
            <div className="space-y-4">
              <div>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Service title *</span>
                <input
                  data-testid="service-new-title"
                  className={inputCls}
                  value={newDraft.title}
                  onChange={(e) => setNewDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="e.g. Drone Light Shows"
                />
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Card description</span>
                <textarea
                  rows={2}
                  data-testid="service-new-desc"
                  className={`${inputCls} resize-none`}
                  value={newDraft.desc}
                  onChange={(e) => setNewDraft((d) => ({ ...d, desc: e.target.value }))}
                />
              </div>
              <div>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Service page introduction</span>
                <textarea
                  rows={4}
                  data-testid="service-new-intro"
                  className={`${inputCls} resize-none`}
                  value={newDraft.intro}
                  onChange={(e) => setNewDraft((d) => ({ ...d, intro: e.target.value }))}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={add}
                  disabled={saving === "new"}
                  data-testid="service-new-save"
                  className="inline-flex items-center gap-2 bg-white text-black px-6 py-2.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors disabled:opacity-50"
                >
                  {saving === "new" && <Loader2 size={13} className="animate-spin" />}
                  {saving === "new" ? "Adding…" : "Add Service"}
                </button>
                <button
                  onClick={() => setAdding(false)}
                  className="border border-white/25 px-6 py-2.5 text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-white hover:border-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {services.map((s, i) => {
          const d = drafts[s.slug] || {};
          return (
            <div
              key={s.slug}
              data-testid={`service-editor-${s.slug}`}
              className="border border-white/10 bg-[#0d0d10] p-5 md:p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                  {String(i + 1).padStart(2, "0")} — {s.slug}
                </span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`service-up-${i}`} aria-label="Move service up" className="border border-white/15 p-2 text-white/60 hover:text-white disabled:opacity-25">
                    <ArrowUp size={13} />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === services.length - 1} data-testid={`service-down-${i}`} aria-label="Move service down" className="border border-white/15 p-2 text-white/60 hover:text-white disabled:opacity-25">
                    <ArrowDown size={13} />
                  </button>
                  <button onClick={() => remove(s)} data-testid={`service-delete-${i}`} aria-label="Delete service" className="border border-white/15 p-2 text-white/60 hover:text-red-400">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
                <div>
                  {d.image ? (
                    <img
                      src={mediaUrl(d.image)}
                      alt={s.title}
                      data-testid={`service-image-preview-${i}`}
                      className="w-full h-32 object-cover border border-white/10 bg-black/40"
                    />
                  ) : (
                    <div className="w-full h-32 border border-white/10 bg-black/40" />
                  )}
                  <div className="mt-2">
                    <MediaUpload
                      label=""
                      kind="image"
                      value={null}
                      onChange={(v) => v && setDrafts((dd) => ({ ...dd, [s.slug]: { ...dd[s.slug], image: v } }))}
                      testId={`service-image-${s.slug}`}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Service title</span>
                    <input
                      data-testid={`service-title-${s.slug}`}
                      className={inputCls}
                      value={d.title || ""}
                      onChange={set(s.slug, "title")}
                    />
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Card description</span>
                    <textarea
                      rows={2}
                      data-testid={`service-desc-${s.slug}`}
                      className={`${inputCls} resize-none`}
                      value={d.desc || ""}
                      onChange={set(s.slug, "desc")}
                    />
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Service page introduction</span>
                    <textarea
                      rows={4}
                      data-testid={`service-intro-${s.slug}`}
                      className={`${inputCls} resize-none`}
                      value={d.intro || ""}
                      onChange={set(s.slug, "intro")}
                    />
                  </div>
                  <button
                    onClick={() => save(s.slug)}
                    disabled={saving === s.slug}
                    data-testid={`service-save-${s.slug}`}
                    className="inline-flex items-center gap-2 bg-white text-black px-6 py-2.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors disabled:opacity-50"
                  >
                    {saving === s.slug && <Loader2 size={13} className="animate-spin" />}
                    {saving === s.slug ? "Saving…" : "Save Service"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServicesTab;
